import React from "react";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import GHGTargetSetForm from "components/GHGTargetSetForm";
import type { GHGTargetForm, GHGTargetGridRow } from "components/GHGTargetSetForm";
import {
  OrgGHGTargetModel,
  OrganizationScope3GHGReduction,
} from "pages/Client/Organization/data/OrgGHGTargetModel";
import {
  _addOrgGHGTarget,
  _deleteOrgGHGTarget,
  _getOrgGHGTargetByTargetNumber,
  _getCustomOrgGHGTargetsByOrgId,
  _updateOrgGHGTarget,
} from "middlewares/OrganizationGHGTargetApi/orgGHGTarget";

// ── adapters ──────────────────────────────────────────────────────────────────

const toBaseForm = (t: OrgGHGTargetModel): GHGTargetForm => ({
  order: t.order,
  targetNumber: t.targetNumber,
  targetName: t.targetName,
  baseYear: t.baseYear,
  targetYear: t.targetYear,
  scopesCovered: t.scopesCovered,
  baselineValue: t.baselineValue,
  targetedValue: t.targetedValue,
  reduction: t.reduction,
  scope1Reduction: t.scope1Reduction,
  scope2Reduction: t.scope2Reduction,
  scope3GHGReduction:
    t.organizationScope3GHGReduction?.map((s) => ({
      id: s.id,
      targetNumber: s.orgGHGTargetTargetNumber,
      catagory: s.catagory,
      catagoryName: s.catagoryName,
      reduction: s.reduction,
      createdBy: s.createdBy,
      modifiedBy: s.modifiedBy,
      createdAt: s.createdAt,
      modifiedAt: s.modifiedAt,
      isActive: s.isActive,
    })) ?? null,
  createdBy: t.createdBy,
  modifiedBy: t.modifiedBy,
  createdAt: t.createdAt,
  modifiedAt: t.modifiedAt,
  isActive: t.isActive,
});

const toOrgModel = (f: GHGTargetForm, entityId: number): OrgGHGTargetModel => ({
  order: f.order,
  targetNumber: f.targetNumber,
  orgID: entityId || null,
  targetName: f.targetName,
  baseYear: f.baseYear,
  targetYear: f.targetYear,
  scopesCovered: f.scopesCovered,
  baselineValue: f.baselineValue,
  targetedValue: f.targetedValue,
  reduction: f.reduction,
  scope1Reduction: f.scope1Reduction,
  scope2Reduction: f.scope2Reduction,
  organizationScope3GHGReduction:
    f.scope3GHGReduction?.map(
      (s): OrganizationScope3GHGReduction => ({
        id: s.id,
        orgGHGTargetTargetNumber: s.targetNumber,
        catagory: s.catagory,
        catagoryName: s.catagoryName,
        reduction: s.reduction,
        createdBy: s.createdBy,
        modifiedBy: s.modifiedBy,
        createdAt: s.createdAt,
        modifiedAt: s.modifiedAt,
        isActive: s.isActive,
      })
    ) ?? null,
  createdBy: f.createdBy,
  modifiedBy: f.modifiedBy,
  createdAt: f.createdAt,
  modifiedAt: f.modifiedAt,
  isActive: f.isActive,
});

// ── props ─────────────────────────────────────────────────────────────────────

interface OrgSetTargetProps {
  orgId: number;
  handleCloseSnackBar: (state: AlertsProps) => void;
}

// ── component ─────────────────────────────────────────────────────────────────

const OrgSetTarget: React.FC<OrgSetTargetProps> = ({ orgId, handleCloseSnackBar }) => (
  <GHGTargetSetForm
    entityId={orgId}
    handleCloseSnackBar={handleCloseSnackBar}
    testIds={{
      addBtn: "btnAddOrgTarget",
      saveBtn: "btnSaveOrgTarget",
      targetName: "orgTargetName",
      scope1: "orgScope1Reduction",
      scope2: "orgScope2Reduction",
      allCategory: "orgAllCategory",
      reductionPct: "orgReductionPercentage",
    }}
    api={{
      fetchGrid: async (id) => {
        const res = await _getCustomOrgGHGTargetsByOrgId(id);
        return (res ?? []) as GHGTargetGridRow[];
      },
      getTarget: async (tn) => {
        const res = await _getOrgGHGTargetByTargetNumber(tn);
        if (!res) return null;
        return toBaseForm(res);
      },
      addTarget: (form, id) => _addOrgGHGTarget(toOrgModel(form, id)),
      updateTarget: (form, id) => _updateOrgGHGTarget(toOrgModel(form, id)),
      deleteTarget: _deleteOrgGHGTarget,
    }}
  />
);

export default OrgSetTarget;
